import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useReactNodeView } from '@lyearn/lygo-core';

export function usePortalForForms<T extends HTMLElement>(
  targetRef: React.RefObject<T>,
  portalContent: React.ReactNode,
  pause: boolean = true,
) {
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);
  const { node, view: editorView, updateAttrs } = useReactNodeView();

  useEffect(() => {
    if (!editorView?.dom?.parentNode || pause) return;

    const parentEl = editorView.dom.parentNode as HTMLElement;

    const container = document.createElement('div');

    container.style.position = 'absolute';
    container.style.zIndex = '2';
    parentEl.style.position = 'relative';
    container.style.top = '0px';

    parentEl.appendChild(container);
    setPortalTarget(container);

    return () => {
      parentEl.removeChild(container);
    };
  }, [editorView, pause]);

  useEffect(() => {
    if (!portalTarget) return;
    let frameId: number;

    function updatePosition() {
      const el = targetRef.current;
      if (el && portalTarget) {
        const rect = el.getBoundingClientRect();
        const parent = editorView?.dom?.parentElement;
        const parentRect = parent?.getBoundingClientRect();

        if (parent) {
          portalTarget.style.top = `${rect.top - (parentRect?.top ?? 0) + parent.scrollTop}px`;
          portalTarget.style.left = `${rect.left - (parentRect?.left ?? 0) + parent.scrollLeft}px`;
        } else {
          portalTarget.style.top = `${rect.top + window.scrollY}px`;
          portalTarget.style.left = `${rect.left + window.scrollX}px`;
        }
        portalTarget.style.width = `${rect.width}px`;
      }
      frameId = requestAnimationFrame(updatePosition);
    }

    updatePosition();
    return () => cancelAnimationFrame(frameId);
  }, [portalTarget, targetRef]);

  return portalTarget ? createPortal(portalContent, portalTarget) : null;
}
